import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { TabbedCodeBlock } from "@/components/TabbedCodeBlock";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { trpc } from "@/lib/trpc";
import { LANGUAGES, type Language, proxySnippets } from "@/snippets";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ArrowRight, Clock, Copy, Key, Loader2, Network, Plus, Save, Trash2, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation, useParams } from "wouter";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function ProxyDetail() {
  const { id } = useParams<{ id: string }>();
  const proxyId = parseInt(id || "0");
  const { user, loading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const [isTokenDialogOpen, setIsTokenDialogOpen] = useState(false);
  const [ttlHours, setTtlHours] = useState(24);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    name: "",
    description: "",
    secretId: 0,
    targetUrl: "",
    authType: "bearer" as "bearer" | "basic" | "api_key" | "custom",
    authHeaderName: "Authorization",
    authPrefix: "",
    rateLimitPerMinute: null as number | null,
  });
  const [settingsDirty, setSettingsDirty] = useState(false);

  const { data: proxy, isLoading, refetch } = trpc.proxy.get.useQuery({ id: proxyId });
  const { data: tokens, refetch: refetchTokens } = trpc.proxy.listTokens.useQuery({ proxyConfigId: proxyId });
  const { data: logs } = trpc.proxy.getLogs.useQuery({ proxyConfigId: proxyId, limit: 50 });
  const { data: stats } = trpc.proxy.getStats.useQuery({ proxyConfigId: proxyId, daysBack: 30 });
  const { data: secretsList } = trpc.secrets.list.useQuery();

  const generateToken = trpc.proxy.generateToken.useMutation({
    onSuccess: (data) => {
      setGeneratedToken(data.token);
      refetchTokens();
      toast.success("Token generated successfully");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteToken = trpc.proxy.deleteToken.useMutation({
    onSuccess: () => {
      refetchTokens();
      toast.success("Token deleted");
    },
  });

  const updateProxy = trpc.proxy.update.useMutation({
    onSuccess: () => {
      refetch();
      setSettingsDirty(false);
      toast.success("Proxy updated");
    },
  });

  // Sync form state when proxy data loads
  useEffect(() => {
    if (proxy) {
      setSettingsForm({
        name: proxy.name,
        description: proxy.description || "",
        secretId: proxy.secretId,
        targetUrl: proxy.targetUrl,
        authType: proxy.authType,
        authHeaderName: proxy.authHeaderName || "Authorization",
        authPrefix: proxy.authPrefix || "",
        rateLimitPerMinute: proxy.rateLimitPerMinute ?? null,
      });
      setSettingsDirty(false);
    }
  }, [proxy]);

  if (authLoading || isLoading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user || !proxy) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">Proxy configuration not found.</p>
        </div>
      </DashboardLayout>
    );
  }

  const handleGenerateToken = () => {
    generateToken.mutate({
      proxyConfigId: proxyId,
      ttlSeconds: ttlHours * 3600,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getApiEndpoint = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/api/proxy/`;
  };

  const activeTokens = tokens?.filter(t => new Date(t.expiresAt) > new Date()) || [];

  const snippetConfig = {
    token: activeTokens[0]?.token || "YOUR_PROXY_TOKEN",
    baseUrl: window.location.origin,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/proxy")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{proxy.name}</h1>
              <Badge variant={proxy.isActive ? "default" : "secondary"}>
                {proxy.isActive ? "Active" : "Disabled"}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              {proxy.targetUrl}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="active-switch" className="text-sm text-muted-foreground">
              {proxy.isActive ? "Enabled" : "Disabled"}
            </Label>
            <Switch
              id="active-switch"
              checked={proxy.isActive}
              onCheckedChange={(checked) => {
                updateProxy.mutate({ id: proxyId, isActive: checked });
              }}
            />
          </div>
        </div>

        {/* How it works diagram */}
        <Card className="bg-card/50">
          <CardContent className="py-4">
            <div className="flex items-center justify-center gap-2 text-sm">
              <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                <span className="font-mono">Your Code</span>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="flex flex-col items-center gap-1 px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg">
                <span className="font-mono text-primary text-xs">{getApiEndpoint()}...</span>
                <span className="text-xs text-muted-foreground">+ Temp Token</span>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                <span className="font-mono text-green-600 text-xs">{proxy.targetUrl}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="tokens" className="space-y-4">
          <TabsList>
            <TabsTrigger value="tokens">Tokens</TabsTrigger>
            <TabsTrigger value="usage">Usage</TabsTrigger>
            <TabsTrigger value="howto">How to Use</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Tokens Tab */}
          <TabsContent value="tokens" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Proxy Tokens</h2>
                <p className="text-sm text-muted-foreground">
                  Generate temporary tokens to authenticate proxy requests
                </p>
              </div>
              <Dialog open={isTokenDialogOpen} onOpenChange={(open) => {
                setIsTokenDialogOpen(open);
                if (!open) setGeneratedToken(null);
              }}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Generate Token
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Generate Proxy Token</DialogTitle>
                    <DialogDescription>
                      Create a temporary token to authenticate proxy requests.
                    </DialogDescription>
                  </DialogHeader>
                  {!generatedToken ? (
                    <>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Token Lifetime</Label>
                          <Select
                            value={String(ttlHours)}
                            onValueChange={(v) => setTtlHours(parseInt(v))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 hour</SelectItem>
                              <SelectItem value="6">6 hours</SelectItem>
                              <SelectItem value="24">24 hours</SelectItem>
                              <SelectItem value="168">7 days</SelectItem>
                              <SelectItem value="720">30 days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setIsTokenDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleGenerateToken} disabled={generateToken.isPending}>
                          {generateToken.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                          Generate
                        </Button>
                      </DialogFooter>
                    </>
                  ) : (
                    <div className="space-y-4 py-4">
                      <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <p className="text-sm text-green-600 mb-2">Token generated successfully!</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-muted p-2 rounded font-mono break-all">
                            {generatedToken}
                          </code>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copyToClipboard(generatedToken)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Save this token securely. You won't be able to see it again.
                      </p>
                      <DialogFooter>
                        <Button onClick={() => {
                          setIsTokenDialogOpen(false);
                          setGeneratedToken(null);
                        }}>
                          Done
                        </Button>
                      </DialogFooter>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {activeTokens.length > 0 ? (
              <div className="space-y-2">
                {activeTokens.map((token) => (
                  <Card key={token.id}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Key className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <code className="text-sm font-mono">
                              {token.token.substring(0, 20)}...
                            </code>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                              <Clock className="h-3 w-3" />
                              <span>
                                Expires {new Date(token.expiresAt).toLocaleString()}
                              </span>
                              <span>•</span>
                              <span>{token.usageCount} uses</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => deleteToken.mutate({ tokenId: token.id })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <Key className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No active tokens</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Usage Tab */}
          <TabsContent value="usage" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Calls
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{proxy.usageCount}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Last 30 Days
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.totalCalls || 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Active Tokens
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{activeTokens.length}</div>
                </CardContent>
              </Card>
            </div>

            {stats && stats.dailyStats.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>API Calls Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stats.dailyStats}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          className="text-xs"
                        />
                        <YAxis className="text-xs" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent logs */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {logs && logs.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {logs.slice(0, 20).map((log) => (
                      <div
                        key={log.id}
                        className="flex items-center justify-between py-2 border-b last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant={log.statusCode && log.statusCode < 400 ? "default" : "destructive"}>
                            {log.method}
                          </Badge>
                          <span className="text-sm font-mono truncate max-w-[300px]">
                            {log.path || "/"}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{log.statusCode}</span>
                          <span>{log.responseTimeMs}ms</span>
                          <span>{new Date(log.timestamp).toLocaleTimeString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No requests yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* How to Use Tab */}
          <TabsContent value="howto" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Python Example</CardTitle>
                <CardDescription>
                  Use this code snippet to make API calls through the proxy
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border">
                  <span className="text-sm text-muted-foreground">First, install the package:</span>
                  <code className="px-2 py-1 bg-muted rounded text-sm font-mono">pip install double-o</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => copyToClipboard("pip install double-o")}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <TabbedCodeBlock snippets={Object.fromEntries(
                  LANGUAGES.map(({ value }) => [value, proxySnippets[value](snippetConfig)])
                ) as Record<Language, string>} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Proxy Endpoint</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Base URL</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                      {getApiEndpoint()}
                    </code>
                    <Button variant="ghost" size="icon" onClick={() => copyToClipboard(getApiEndpoint())}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Target API</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                      {proxy.targetUrl}
                    </code>
                  </div>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>How it works:</strong> Append your API path to the proxy URL.
                    For example, to call <code>{proxy.targetUrl}/v1/chat/completions</code>,
                    send your request to <code>{getApiEndpoint()}v1/chat/completions</code>
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Configuration</CardTitle>
                  {settingsDirty && (
                    <Button
                      size="sm"
                      onClick={() => {
                        updateProxy.mutate({
                          id: proxyId,
                          name: settingsForm.name,
                          description: settingsForm.description || undefined,
                          secretId: settingsForm.secretId,
                          targetUrl: settingsForm.targetUrl,
                          authType: settingsForm.authType,
                          authHeaderName: settingsForm.authHeaderName,
                          authPrefix: settingsForm.authPrefix || undefined,
                          rateLimitPerMinute: settingsForm.rateLimitPerMinute,
                        });
                      }}
                      disabled={updateProxy.isPending}
                    >
                      {updateProxy.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={settingsForm.name}
                      onChange={(e) => {
                        setSettingsForm(f => ({ ...f, name: e.target.value }));
                        setSettingsDirty(true);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secret</Label>
                    <Select
                      value={String(settingsForm.secretId)}
                      onValueChange={(v) => {
                        setSettingsForm(f => ({ ...f, secretId: parseInt(v) }));
                        setSettingsDirty(true);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {secretsList?.map((s) => (
                          <SelectItem key={s.id} value={String(s.id)}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={settingsForm.description}
                      onChange={(e) => {
                        setSettingsForm(f => ({ ...f, description: e.target.value }));
                        setSettingsDirty(true);
                      }}
                      placeholder="Optional description"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Target URL</Label>
                    <Input
                      value={settingsForm.targetUrl}
                      onChange={(e) => {
                        setSettingsForm(f => ({ ...f, targetUrl: e.target.value }));
                        setSettingsDirty(true);
                      }}
                      placeholder="https://api.example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Auth Type</Label>
                    <Select
                      value={settingsForm.authType}
                      onValueChange={(v) => {
                        setSettingsForm(f => ({ ...f, authType: v as typeof f.authType }));
                        setSettingsDirty(true);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bearer">Bearer</SelectItem>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="api_key">API Key</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Auth Header Name</Label>
                    <Input
                      value={settingsForm.authHeaderName}
                      onChange={(e) => {
                        setSettingsForm(f => ({ ...f, authHeaderName: e.target.value }));
                        setSettingsDirty(true);
                      }}
                      placeholder="Authorization"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Auth Prefix</Label>
                    <Input
                      value={settingsForm.authPrefix}
                      onChange={(e) => {
                        setSettingsForm(f => ({ ...f, authPrefix: e.target.value }));
                        setSettingsDirty(true);
                      }}
                      placeholder="Bearer"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rate Limit (requests/min)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={10000}
                        value={settingsForm.rateLimitPerMinute ?? ""}
                        onChange={(e) => {
                          const val = e.target.value ? parseInt(e.target.value) : null;
                          setSettingsForm(f => ({ ...f, rateLimitPerMinute: val }));
                          setSettingsDirty(true);
                        }}
                        placeholder="Unlimited"
                      />
                      {settingsForm.rateLimitPerMinute !== null && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSettingsForm(f => ({ ...f, rateLimitPerMinute: null }));
                            setSettingsDirty(true);
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Leave empty for unlimited. Applies per proxy configuration across all tokens.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
